import { useCallback, useEffect, useMemo, useState } from "react";
import TextInput from "../../components/Select/InputText";
import TableLoadingRow from "../../components/TableLoadingRow";
import { useAuth } from "../../context/AuthContext";
import { getPackageMonthlyAnalytics } from "../../services/packagesService";

type PackageAnalyticsRow = {
    _id: string;
    name: string;
    price: number;
    durationValue: number;
    durationUnit: string;
    isPtSession: boolean;
    numberOfPtSessions: number;
    monthlyStats?: {
        soldCount: number;
        activeCount: number;
        monthlyReturn: number;
    };
};

type PackageAnalyticsTotals = {
    soldCount: number;
    activeCount: number;
    monthlyReturn: number;
};

const emptyTotals: PackageAnalyticsTotals = {
    soldCount: 0,
    activeCount: 0,
    monthlyReturn: 0,
};

const currentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const asNumber = (value: unknown, fallback = 0) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
};

const formatMoney = (value: unknown) =>
    asNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

const readArray = (value: unknown): unknown[] => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
        const data = value as { rows?: unknown; data?: unknown; items?: unknown };
        if (Array.isArray(data.rows)) return data.rows;
        if (Array.isArray(data.data)) return data.data;
        if (Array.isArray(data.items)) return data.items;
    }
    return [];
};

const readErrorMessage = (err: unknown, fallback: string): string => {
    if (!err || typeof err !== "object") return fallback;
    const errObj = err as { response?: { data?: { message?: string; error?: string } } };
    return errObj.response?.data?.message ?? errObj.response?.data?.error ?? fallback;
};

const normalizeRow = (entry: unknown): PackageAnalyticsRow => {
    const row = entry && typeof entry === "object" ? entry as Record<string, any> : {};
    const stats = row.monthlyStats || {};

    return {
        _id: String(row._id ?? row.id ?? row.name ?? crypto.randomUUID()),
        name: String(row.name ?? "-"),
        price: asNumber(row.price),
        durationValue: asNumber(row.durationValue),
        durationUnit: String(row.durationUnit ?? ""),
        isPtSession: Boolean(row.isPtSession),
        numberOfPtSessions: asNumber(row.numberOfPtSessions),
        monthlyStats: {
            soldCount: asNumber(stats.soldCount),
            activeCount: asNumber(stats.activeCount),
            monthlyReturn: asNumber(stats.monthlyReturn),
        },
    };
};

const PackageAnalyticsPage = () => {
    const { user } = useAuth();
    const initialMonth = useMemo(() => currentMonth(), []);
    const [month, setMonth] = useState(initialMonth);
    const [rows, setRows] = useState<PackageAnalyticsRow[]>([]);
    const [totals, setTotals] = useState<PackageAnalyticsTotals>(emptyTotals);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchAnalytics = useCallback(async () => {
        if (!user?.id) return;

        setLoading(true);
        setError("");
        try {
            const response = await getPackageMonthlyAnalytics(month);
            setRows(readArray(response).map(normalizeRow));
            setTotals({
                soldCount: asNumber(response?.totals?.soldCount),
                activeCount: asNumber(response?.totals?.activeCount),
                monthlyReturn: asNumber(response?.totals?.monthlyReturn),
            });
        } catch (err: unknown) {
            console.error("Failed to load package analytics", err);
            setError(readErrorMessage(err, "Failed to load package analytics"));
        } finally {
            setLoading(false);
        }
    }, [user?.id, month]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    return (
        <div className="w-full p-6">
            <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Package Analytics</h1>
                    <p className="text-sm text-gray-500">Monthly package sales, active subscriptions, and return.</p>
                </div>
                <button
                    type="button"
                    onClick={fetchAnalytics}
                    disabled={loading}
                    className="w-fit rounded-md bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                    {loading ? "Loading..." : "Refresh"}
                </button>
            </div>

            {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

            <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-4">
                <TextInput
                    label="Month"
                    type="month"
                    name="month"
                    value={month}
                    onChange={(event) => setMonth(event.target.value)}
                />
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Sold This Month</p>
                    <p className="mt-2 text-2xl font-bold">{loading ? "..." : totals.soldCount.toLocaleString()}</p>
                </div>
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Active This Month</p>
                    <p className="mt-2 text-2xl font-bold">{loading ? "..." : totals.activeCount.toLocaleString()}</p>
                </div>
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Monthly Return</p>
                    <p className="mt-2 text-2xl font-bold text-[#ac120c]">{loading ? "..." : formatMoney(totals.monthlyReturn)}</p>
                </div>
            </div>

            <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-black text-white">
                        <tr>
                            <th className="p-3 text-left">Package</th>
                            <th className="p-3 text-right">Price</th>
                            <th className="p-3 text-left">Duration</th>
                            <th className="p-3 text-right">Sold</th>
                            <th className="p-3 text-right">Active</th>
                            <th className="p-3 text-right">Monthly Return</th>
                            <th className="p-3 text-left">PT</th>
                            <th className="p-3 text-right">PT Sessions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <TableLoadingRow colSpan={8} label="Loading monthly analytics..." />
                        ) : rows.length ? (
                            rows.map((row) => (
                                <tr key={row._id} className="border-t border-gray-200 hover:bg-gray-50">
                                    <td className="p-3 font-medium">{row.name}</td>
                                    <td className="p-3 text-right">{formatMoney(row.price)}</td>
                                    <td className="p-3">{row.durationValue} {row.durationUnit}</td>
                                    <td className="p-3 text-right">{row.monthlyStats?.soldCount.toLocaleString()}</td>
                                    <td className="p-3 text-right">{row.monthlyStats?.activeCount.toLocaleString()}</td>
                                    <td className="p-3 text-right font-semibold">{formatMoney(row.monthlyStats?.monthlyReturn)}</td>
                                    <td className="p-3">{row.isPtSession ? "Yes" : "No"}</td>
                                    <td className="p-3 text-right">{row.numberOfPtSessions.toLocaleString()}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td className="p-4 text-center text-gray-500" colSpan={8}>No packages found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PackageAnalyticsPage;
